/**
 * BattleCode V3 — Engine shared types (multi-tank support)
 *
 * Engine 由 3 个层次构成：
 *   1. Main thread (EngineScheduler) — 协调 + 渲染 + 30Hz tick
 *   2. Pyodide Worker (×2) — 每个 bot 一个 Worker，独立 Pyodide 实例
 *   3. Bot code (Python) — think() 函数 + 15 个玩家可见 self API
 *     (V3: 5 状态 + 2 工具方法 + 3 工具属性 + 3 动作 + 2 新增 ally_tanks/tank_index)
 *
 * V3 Multi-Tank (2026-07-12):
 *   - TankState 加 tank_index 字段
 *   - TeamState 新增（tanks[] 数组）
 *   - BattleState 加 team_a/team_b（向后兼容 self/enemy getter）
 *   - BotAction.shoot 加 target_index 可选
 *   - EngineEvent 扩展多坦克 damage/hit 格式
 */

// ===== 基础几何 =====
export interface Position {
  x: number;
  y: number;
}

export type TileType = "empty" | "obstacle" | "cover" | "spawn" | "unreachable";

export interface Tile {
  type: TileType;
  blocks_movement: boolean;
  blocks_vision: boolean;
  damage_reduction: number;
}

// ===== 战斗状态 =====
export interface TankState {
  team: "team_a" | "team_b";
  /** V3: 队内序号 0-based，1v1 时固定为 0 */
  tank_index: number;
  position: Position;
  /** FR-022 ADR-022.2:出生点,开局固定,整个对局不变。*/
  spawn: Position;
  health: number;
  max_health: number;
  cooldown_left: number;
  attack_cooldown_ticks: number;
  is_alive: boolean;
  /** V3: 多坦克模式下此坦克是否被敌方发现（每 tick 派生） */
  is_visible?: boolean;
}

/**
 * FR-018 ADR-018.2:
 *   is_alive  = 真实生死（HP > 0）
 *   is_visible = 是否看到对方（与 is_alive 解耦）
 *
 * FR-022 ADR-022.2:加 spawn 字段
 * V3: 多坦克下 enemy 可能不可见或不存在，用 is_alive 判断存活
 */
export interface EnemyState {
  /** V3: 敌方坦克的队内序号 */
  tank_index: number;
  position: Position;
  spawn: Position;
  health: number;
  max_health: number;
  is_alive: boolean;
  is_visible: boolean;
  last_known_position: Position | null;
}

export interface CoverInfo {
  position: Position;
  distance: number;
}

/** V3: 队伍状态 — 替代 V1/V2 的 self/enemy 单坦克模型 */
export interface TeamState {
  tanks: TankState[];
  units_per_team: number;
}

export interface BattleState {
  tick: number;

  // ===== V1/V2 单坦克字段（向后兼容，1v1 时使用）=====
  self: TankState;
  enemy: EnemyState;
  nearest_cover: CoverInfo | null;

  // ===== V3 多坦克字段（max_units_per_team > 1 时使用）=====
  /** V3: team_a 状态（包含本队所有坦克） */
  team_a?: TeamState;
  /** V3: team_b 状态（包含敌方所有坦克） */
  team_b?: TeamState;

  map_id: string;
  map_size: { width: number; height: number };
  /** FR-018 ADR-018.4：shoot/scan 超距反馈 */
  warning?: string | null;
  /**
   * P0 修复(2026-07-09):AI 视角单独追踪玩家位置的字段
   */
  ai_known_player_position?: Position | null;
}

// ===== Bot 输出 =====
export type BotAction =
  | { type: "move"; target: Position }
  | { type: "shoot"; /** V3: 可选目标序号，默认最近可见敌人 */ target_index?: number }
  | { type: "scan" }
  | { type: "idle" };

/** V3: 多坦克 tick 结果 — 每坦克一个 action */
export interface BotTickResult {
  /** V1/V2: 单坦克 action（向后兼容） */
  action: BotAction;
  /** V3: 多坦克 actions（每个 tank 一个 action） */
  actions?: BotAction[];
  logs: string[];
  /** V3: 多坦克 per-tank 日志（tank_logs[i] = 第 i 个 tank 的日志行，不含 [tankN] 前缀） */
  tank_logs?: string[][];
  /** V3: 坦克间消息发件箱（message_outbox[i] = 第 i 个 tank 发出的消息列表） */
  message_outbox?: Array<Array<{ to: number; data: any }>>;
  duration_ms: number;
  error: string | null;
  timed_out: boolean;
}

// ===== Worker 消息协议 =====
export type WorkerInbound =
  | { type: "init"; botCode: string; /** V3: 多坦克代码数组 */ botCodes?: string[]; isAI: boolean; botId: string; mode?: string }
  | { type: "tick"; state: BattleState; tickNum: number; /** V3: 该队坦克数 */ unitsPerTeam?: number }
  | { type: "tick_batch"; state: BattleState; tickNum: number; /** V3: 本队 tank 序号列表 */ tankIndices?: number[]; /** V3: 坦克消息收件箱 message_inbox[tankIndex] = [{from, data}] */ message_inbox?: Record<number, Array<{ from: number; data: any }>> }
  | { type: "shutdown" };

export type WorkerOutbound =
  | { type: "ready"; botId: string }
  | { type: "init_done"; botId: string; duration_ms: number; error: string | null }
  | { type: "tick_result"; botId: string; tickNum: number; result: BotTickResult }
  | { type: "error"; botId: string; error: string; phase: "init" | "tick" }
  | { type: "diag_log"; level: "info" | "warn" | "error"; msg: string; botId: string }
  | { type: "stdout"; botId: string; chunk: string }
  | { type: "stderr"; botId: string; chunk: string };

// ===== Pre-flight =====
export interface PreFlightTick {
  state: BattleState;
  expected_action: BotAction;
  label: string;
}

export interface PreFlightResult {
  passed: boolean;
  total_duration_ms: number;
  perTick: Array<{
    tick: number;
    label: string;
    playerLabel?: string;
    description?: string;
    passed: boolean;
    duration_ms: number;
    action: BotAction;
    error: string | null;
  }>;
  errors: string[];
}

// ===== Engine 公共 API（main thread）=====
export interface EngineConfig {
  tick_rate_hz: number;
  soft_timeout_ms: number;
  hard_timeout_ms: number;
  pre_flight_max_total_ms: number;
  runtime_error_threshold: number;
  debug: boolean;
}

export const DEFAULT_ENGINE_CONFIG: EngineConfig = {
  tick_rate_hz: 10,        // V14: 30→10Hz 逻辑 tick（100ms），渲染由独立 rAF loop 驱动
  soft_timeout_ms: 50,
  hard_timeout_ms: 200,
  pre_flight_max_total_ms: 2500,
  runtime_error_threshold: 3,
  debug: false,
};

export type EngineEvent =
  | { type: "tick"; tickNum: number; state: BattleState;
      /** V1/V2: 单坦克 actions（向后兼容） */
      actions: { team_a: BotAction; team_b: BotAction };
      /** V3: 多坦克 actions（数组形式） */
      actionsMulti?: { team_a: BotAction[]; team_b: BotAction[] };
      logs: { team_a: string[]; team_b: string[] };
      damage: { team_a_to_team_b: number; team_b_to_team_a: number };
      /** V3: 多坦克 damage（每坦克的伤害） */
      damageMulti?: { team_a_to_team_b: number[]; team_b_to_team_a: number[] };
      /** V3: 多坦克 per-tank 日志（tankLogs.team_a[i] = 第 i 个 tank 的日志行） */
      tankLogs?: { team_a: string[][]; team_b: string[][] };
    }
  | { type: "hit"; tickNum: number; shooter: "team_a" | "team_b"; target: "team_a" | "team_b";
      /** V3: 多坦克 shooter/target 索引 */
      shooterIndex?: number; targetIndex?: number;
      damage: number; killed: boolean }
  | { type: "battle_end"; winner: "team_a" | "team_b" | "draw"; reason: string }
  | { type: "error"; source: "player" | "ai" | "engine"; errorCode: string; message: string; tickNum: number }
  | { type: "key_event"; eventType: "DEATH" | "FIRST_BLOOD" | "COOLDOWN_TRIGGER" | "COVER_USE" | "SCAN_DISCOVERY" | "COLLISION" | "SCAN_FAILED" | "SHOOT_FAILED" | "ACTION_BLOCKED";
      tickNum: number; actor: "team_a" | "team_b";
      /** V3: 多坦克 actor 索引 */
      actorIndex?: number;
      description: string }
  | { type: "loading_progress"; stage: string };

export type EngineEventListener = (event: EngineEvent) => void;

// === V14: 战斗动效事件（屏 4 → 渲染器桥接） ===

/** 单次伤害事件 — 用于 spawn damage number + hit stop + shake */
export interface VfxDamageEvent {
  targetPos: Position;
  damage: number;
  tickNum: number;
}

/** 射击事件 — 用于 spawn 弹丸飞行动画 */
export interface VfxShootEvent {
  from: Position;
  to: Position;
  shooterTeam: "team_a" | "team_b";
  tickNum: number;
}

/** 击杀事件 — 用于 spawn 碎片爆发 + 击杀标注 */
export interface VfxKillEvent {
  pos: Position;
  killedTeam: "team_a" | "team_b";
  tickNum: number;
}

/** 一帧内累积的动效事件（每个逻辑 tick 收集一次） */
export interface VfxFrameEvents {
  damage: VfxDamageEvent[];
  shoots: VfxShootEvent[];
  kills: VfxKillEvent[];
}

// ===== Phase 1: Rules 数据层（rules.json 格式）=====
export type GameModeType = "combat" | "exploration" | "puzzle" | "teaching";

export type WinConditionType = "eliminate" | "timeout" | "reach_zone";

export interface WinCondition {
  type: WinConditionType;
  tick?: number;
  radius?: number;
  target?: "enemy" | Position;
}

export type RefereePersonality = "transparent" | "hint" | "strict";

export type ViolationType =
  | "invalid_move"
  | "cooldown_shoot"
  | "out_of_range"
  | "blocked_api";

export interface ViolationHandling {
  visibility: "silent" | "hint" | "strict";
  feedback_channel: "kill_feed" | "console" | "both";
}

export interface RefereeConfig {
  personality: RefereePersonality;
  violation_handling: Partial<Record<ViolationType, ViolationHandling>>;
}

export interface ApiSurface {
  base_apis: string[];
  available_apis: string[];
  blocked_apis: string[];
}

export interface RulesConfig {
  rule_id: string;
  mode: GameModeType;
  win_conditions: WinCondition[];
  constraints: {
    /** V3: 每队最大坦克数（1-3），控制 1v1/2v2/3v3 */
    max_units_per_team: number;
    friendly_fire: boolean;
  };
  api_surface: ApiSurface;
  referee: RefereeConfig;
}
